// AI.3B — Card de sugestão com bloqueio crítico + regeneração contextual.
//
// - Nunca aplica automaticamente.
// - Bloqueia aplicação (sem bypass) quando conflitos CRÍTICOS são detectados.
// - Warning ⇒ pede confirmação consciente antes de aplicar.
// - Conflito de concorrência ⇒ diff lado a lado.
// - Regeneração com seletor de motivo (mais específico, mais curto, etc).

import { useState } from "react";
import { AlertOctagon, AlertTriangle, Check, Copy, Edit3, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { applyAISuggestion, regenerateAISuggestion, rejectAISuggestion } from "@/lib/ai/consultant.functions";
import { recordSuggestionOutcome, estimateEditMagnitude } from "@/lib/ai/homologation.functions";
import { HomologationFeedbackPrompt } from "./HomologationFeedbackPrompt";
import { useAIConsultant } from "./AIConsultantProvider";

type ConfidenceLevel = "high" | "medium" | "low";
type ConsistencyIssue = { id: string; title: string; description: string; evidence?: string; suggestedResolution?: string; sections?: string[]; fields?: string[] };

const CONFIDENCE_LABEL: Record<ConfidenceLevel, string> = {
  high: "confiança alta",
  medium: "confiança média",
  low: "confiança baixa — vale validar",
};
const CONFIDENCE_STYLE: Record<ConfidenceLevel, string> = {
  high: "bg-[var(--green)]/10 text-[var(--green)]",
  medium: "bg-[var(--gold)]/15 text-[var(--gold)]",
  low: "bg-black/5 text-[var(--gray-ink)]",
};

const REGEN_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "more_specific", label: "Mais específico" },
  { key: "shorter", label: "Mais curto" },
  { key: "adapt_audience", label: "Adaptar ao público" },
  { key: "fix_inconsistency", label: "Corrigir inconsistência" },
  { key: "other_approach", label: "Outra abordagem" },
];

export function AISuggestionCard({
  suggestionId,
  text,
  confidence,
  warnings,
  onApplied,
  conversationId,
  sectionKey,
  fieldKey,
}: {
  suggestionId: string;
  text: string;
  confidence?: ConfidenceLevel;
  warnings?: string[];
  onApplied?: (finalText: string) => void;
  conversationId?: string | null;
  sectionKey?: string | null;
  fieldKey?: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(text);
  const [saving, setSaving] = useState(false);
  const [applied, setApplied] = useState(false);
  const [conflict, setConflict] = useState<{ currentValue: string; suggestionText: string } | null>(null);
  const [critical, setCritical] = useState<ConsistencyIssue[] | null>(null);
  const [confirmWarn, setConfirmWarn] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [rejectMenuOpen, setRejectMenuOpen] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [showApplyFeedback, setShowApplyFeedback] = useState(false);

  const apply = useServerFn(applyAISuggestion);
  const reject = useServerFn(rejectAISuggestion);
  const regen = useServerFn(regenerateAISuggestion);
  const recordOutcome = useServerFn(recordSuggestionOutcome);
  const qc = useQueryClient();
  const consultant = useAIConsultant();

  const conf = (confidence ?? "medium") as ConfidenceLevel;
  const hasWarnings = (warnings?.length ?? 0) > 0;

  async function handleApply(useEdited: boolean, force = false) {
    if (hasWarnings && !confirmWarn && !force && !applied) {
      setConfirmWarn(true);
      return;
    }
    setSaving(true);
    try {
      const finalText = useEdited ? edited : text;
      const res: any = await apply({
        data: { suggestionId, editedText: useEdited ? finalText : null, force },
      });
      if (res?.criticalConflict) {
        setCritical(res.issues ?? []);
        setConflict(null);
        return;
      }
      if (res?.conflict) {
        setConflict({ currentValue: res.currentValue ?? "", suggestionText: res.suggestionText });
        return;
      }
      setApplied(true);
      setConflict(null);
      setCritical(null);
      qc.invalidateQueries({ queryKey: ["business-plan-bundle"] });
      qc.invalidateQueries({ queryKey: ["business-workspace"] });
      qc.invalidateQueries({ queryKey: ["consistency-report"] });
      toast.success("Sugestão aplicada ao seu plano.");
      onApplied?.(finalText);
      setShowApplyFeedback(true);
      try {
        const magnitude = useEdited ? estimateEditMagnitude(text, finalText) : null;
        void recordOutcome({
          data: {
            suggestionId,
            outcome: useEdited ? "applied_after_edit" : "applied",
            editMagnitude: magnitude,
            sectionKey: sectionKey ?? null,
            fieldKey: fieldKey ?? null,
          },
        });
      } catch { /* telemetry only */ }
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível aplicar.");
    } finally {
      setSaving(false);
    }
  }

  async function handleReject(reason?: string) {
    try {
      await reject({ data: { suggestionId } });
      setApplied(true);
      setRejected(true);
      setRejectMenuOpen(false);
      toast.success("Sugestão descartada.");
      try {
        void recordOutcome({
          data: {
            suggestionId,
            outcome: "rejected",
            rejectReason: (reason ?? null) as any,
            sectionKey: sectionKey ?? null,
            fieldKey: fieldKey ?? null,
          },
        });
      } catch { /* telemetry only */ }
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível descartar.");
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(editing ? edited : text);
      toast.success("Copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  }

  async function handleRegen(reason: string) {
    setRegenOpen(false);
    setRegenerating(true);
    try {
      const res = await regen({
        data: {
          suggestionId,
          reason,
          previousWarnings: warnings ?? [],
          conflictIds: critical?.map((c) => c.id) ?? [],
        },
      });
      // Abrir consultor com a mesma conversa; o usuário vê a nova sugestão chegar.
      consultant.open({
        conversationId: res.conversationId ?? conversationId ?? null,
        sectionKey: sectionKey ?? null,
        fieldKey: fieldKey ?? null,
        intent: "field_suggestion",
      });
      toast.info("Regenerando com base no seu motivo.");
      setApplied(true); // trava este card antigo
      try {
        void recordOutcome({
          data: {
            suggestionId,
            outcome: "regenerated",
            sectionKey: sectionKey ?? null,
            fieldKey: fieldKey ?? null,
          },
        });
      } catch { /* telemetry only */ }
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível regenerar.");
    } finally {
      setRegenerating(false);
    }
  }

  function goToSection(k?: string) {
    if (!k) return;
    consultant.close();
    // hard-navigate — o drawer estará fechado.
    window.location.assign(`/membros/meu-negocio/secao/${k}`);
  }

  return (
    <div
      className={`mt-2 rounded-xl border p-3 ${
        critical?.length
          ? "border-red-300 bg-red-50/60"
          : "border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/[0.06] to-white"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--navy)]">
          <Sparkles size={12} className="text-[var(--gold)]" />
          Sugestão para o seu plano
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${CONFIDENCE_STYLE[conf]}`}>
          {CONFIDENCE_LABEL[conf]}
        </span>
      </div>

      {hasWarnings && (
        <ul className="mt-2 space-y-0.5 text-[11px] text-[var(--gold)]">
          {warnings!.map((w, i) => (
            <li key={i} className="flex items-start gap-1">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {w}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <textarea
          value={edited}
          onChange={(e) => setEdited(e.target.value)}
          rows={Math.min(10, Math.max(3, Math.ceil(edited.length / 60)))}
          className="mt-2 w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
        />
      ) : (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[var(--navy)]">{text}</p>
      )}

      {/* Bloqueio crítico — sem bypass */}
      {critical && critical.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-300 bg-white p-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700">
            <AlertOctagon size={13} /> Aplicação bloqueada por inconsistência crítica
          </p>
          <ul className="mt-2 space-y-2">
            {critical.map((c) => (
              <li key={c.id} className="rounded-md bg-red-50 p-2 text-[12px] text-red-900">
                <p className="font-semibold">{c.title}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-red-900/80">{c.description}</p>
                {c.suggestedResolution && (
                  <p className="mt-1 text-[11px] italic text-red-900/70">→ {c.suggestedResolution}</p>
                )}
                {c.sections && c.sections.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {c.sections.slice(0, 3).map((s) => (
                      <button
                        key={s}
                        onClick={() => goToSection(s)}
                        className="rounded border border-red-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-red-800 hover:bg-red-100"
                      >
                        Ver seção · {s}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-red-800 hover:bg-red-50"
            >
              <Edit3 size={11} className="mr-1 inline" /> Editar sugestão
            </button>
            <button
              type="button"
              onClick={() => setRegenOpen((v) => !v)}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-red-800 hover:bg-red-50"
            >
              <RefreshCw size={11} className="mr-1 inline" /> Regenerar
            </button>
          </div>
        </div>
      )}

      {/* Conflito de concorrência */}
      {conflict && !critical && (
        <div className="mt-3 rounded-lg border border-[var(--gold)]/40 bg-white p-3">
          <p className="flex items-center gap-1 text-[11px] font-semibold text-[var(--gold)]">
            <AlertTriangle size={12} /> Este campo mudou depois que a sugestão foi gerada.
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="rounded-md bg-black/[0.03] p-2">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Valor atual</p>
              <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--navy)]">{conflict.currentValue || "—"}</p>
            </div>
            <div className="rounded-md bg-[var(--gold)]/[0.08] p-2">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Sugestão da IA</p>
              <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--navy)]">{conflict.suggestionText}</p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button" disabled={saving}
              onClick={() => handleApply(editing, true)}
              className="rounded-md bg-[var(--navy)] px-3 py-1.5 text-[11px] font-semibold text-white hover:-translate-y-0.5 transition disabled:opacity-50"
            >
              Substituir mesmo assim
            </button>
            <button
              type="button"
              onClick={() => setConflict(null)}
              className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
            >
              Manter atual
            </button>
          </div>
        </div>
      )}

      {/* Confirmação de warnings antes de aplicar */}
      {confirmWarn && !applied && !critical && !conflict && (
        <div className="mt-3 rounded-lg border border-[var(--gold)]/40 bg-[var(--gold)]/[0.08] p-3">
          <p className="text-[11px] font-semibold text-[var(--navy)]">
            Aplicar mesmo com os avisos acima?
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button" disabled={saving}
              onClick={() => handleApply(editing, true)}
              className="rounded-md bg-[var(--navy)] px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : "Sim, aplicar"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmWarn(false)}
              className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
            >
              Rever
            </button>
          </div>
        </div>
      )}

      {/* Menu de regeneração */}
      {regenOpen && (
        <div className="mt-3 rounded-lg border border-black/10 bg-white p-3">
          <p className="text-[11px] font-semibold text-[var(--gray-ink)]">Por que regenerar?</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {REGEN_OPTIONS.map((r) => (
              <button
                key={r.key}
                type="button"
                disabled={regenerating}
                onClick={() => handleRegen(r.key)}
                className="rounded-md border border-black/10 bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--navy)] hover:bg-black/[0.03] disabled:opacity-50"
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {applied ? (
          <span className="inline-flex items-center gap-1 rounded-lg bg-[var(--green)]/12 px-3 py-1.5 text-xs font-semibold text-[var(--green)]">
            <Check size={12} /> {critical ? "Substitua ou regenere para prosseguir" : "Aplicada ao plano"}
          </span>
        ) : (
          <>
            {!critical && (
              <button
                type="button" disabled={saving}
                onClick={() => handleApply(editing)}
                className="inline-flex items-center gap-1 rounded-lg bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:-translate-y-0.5 transition disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {editing ? "Salvar edição" : "Usar esta sugestão"}
              </button>
            )}
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
              >
                <Edit3 size={12} /> Editar antes de salvar
              </button>
            )}
            <button
              type="button"
              onClick={() => setRegenOpen((v) => !v)}
              disabled={regenerating}
              className="inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03] disabled:opacity-50"
            >
              {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Gerar outra
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
            >
              <Copy size={12} /> Copiar
            </button>
            <button
              type="button"
              onClick={() => setRejectMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)] hover:bg-black/[0.03]"
            >
              <X size={12} /> Descartar
            </button>
          </>
        )}
      </div>

      {rejectMenuOpen && !rejected && (
        <div className="mt-3 rounded-lg border border-black/10 bg-white p-3">
          <p className="text-[11px] font-semibold text-[var(--gray-ink)]">Por que descartar? (opcional)</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              { key: "not_my_business", label: "Não representa meu negócio" },
              { key: "too_generic", label: "Muito genérica" },
              { key: "too_long", label: "Muito longa" },
              { key: "too_short", label: "Muito curta" },
              { key: "incorrect_info", label: "Informação incorreta" },
              { key: "didnt_understand", label: "Não entendi" },
              { key: "prefer_write_alone", label: "Prefiro escrever sozinho" },
              { key: "other", label: "Outro" },
            ].map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => handleReject(r.key)}
                className="rounded-md border border-black/10 bg-white px-2.5 py-1 text-[11px] font-semibold text-[var(--navy)] hover:bg-black/[0.03]"
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => handleReject()}
              className="rounded-md border border-black/10 bg-white px-3 py-1 text-[11px] font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)]"
            >
              Descartar sem motivo
            </button>
          </div>
        </div>
      )}

      {showApplyFeedback && applied && !critical && (
        <div className="mt-3">
          <HomologationFeedbackPrompt
            trigger="first_applied"
            projectId={null}
            context={{ sectionKey: sectionKey ?? null, fieldKey: fieldKey ?? null }}
            onDone={() => setShowApplyFeedback(false)}
          />
        </div>
      )}
    </div>
  );
}

